import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import PropTypes from "prop-types";
import Button3px from "../../Generals/Buttons/3pxButton";
import TitledSelectorSlim from "../../Generals/Selectors/TitledSelectorSlim";
import TitledInput from "../../Generals/Inputs/TitledInput";
import {
  actionsSx,
  cancelButtonSx,
  continueButtonProps,
  statementDialogSx,
  statementDialogPaperSx,
  statementHeaderTitleSx,
  statementCloseButtonSx,
  statementCardBoxSx,
  statementLabelMutedSx,
  statementValueSx,
  statementStatusLabelSx,
  statementSectionTitleSx,
  statementDetailsGridSx,
  statementReasonTitleSx,
} from "./FormModalStyles";
import Colors from "../../../assets/Variables/ColorsVariables";

const StatementRequestModal = ({
  open,
  title = "Statement Request",
  fields,
  onSubmit,
  onClose,
  confirmLabel = "Save Changes",
  cancelLabel = "Cancel",
}) => {
  const [formState, setFormState] = useState({});

  useEffect(() => {
    if (fields) {
      const initial = {};
      fields.forEach((f) => {
        initial[f.name] = f.value ?? "";
      });
      setFormState(initial);
    }
  }, [fields]);

  const handleChange = (e) => {
    setFormState((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = () => {
    if (onSubmit) onSubmit(formState);
  };

  const getField = (name) => fields?.find((f) => f.name === name);

  const renderTextRow = (label, value) => (
    <Box sx={{ mb: 1.25 }}>
      <Typography sx={statementLabelMutedSx}>{label}</Typography>
      <Typography sx={statementValueSx}>{value || "-"}</Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} sx={statementDialogSx} PaperProps={{ sx: statementDialogPaperSx }}>
      {/* HEADER */}
      <DialogTitle sx={{ p: 0, pb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={statementHeaderTitleSx}>{title}</Typography>
          <Button onClick={onClose} sx={statementCloseButtonSx}>×</Button>
        </Box>
      </DialogTitle>

      {/* CONTENT */}
      <DialogContent sx={{ p: 0, overflowY: "auto", height: "486px" }}>
        {/* CARD SUPERIOR - TitledSelectorSlim */}
        <Box sx={statementCardBoxSx}>
          {/* LEFT COLUMN */}
          <Box>
            {renderTextRow("Request ID:", formState.request_id)}
            {renderTextRow("Submitted on:", formState.submitted_on)}
          </Box>
          
          {/* RIGHT COLUMN */}
          <Box>
            {(() => {
              const field = getField("status");
              if (!field) return renderTextRow("Status:", formState.status);
              return (
                <Box sx={{ mb: 0.5 }}>
                  <Typography sx={statementStatusLabelSx}>Status:</Typography>
                  <Box sx={{ mt: -0.5 }}>
                    <TitledSelectorSlim
                      title=""
                      name={field.name}
                      value={formState[field.name]}
                      handleChange={handleChange}
                      options={field.options || []}
                      placeholder=""
                      height="24px"
                      width="110px"
                    />
                  </Box>
                </Box>
              );
            })()}
            {renderTextRow("Requested by:", formState.requested_by)}
          </Box>
        </Box>

        {/* DETAILS */}
        <Typography sx={statementSectionTitleSx}>Details</Typography>
        <Box sx={statementDetailsGridSx}>
          {/* LEFT COLUMN */}
          <Box>
            {renderTextRow("Signal Name:", formState.signal_name)}
            {renderTextRow("Signal Login:", formState.signal_login)}
            {renderTextRow("External Broker:", formState.external_broker)}
            {renderTextRow("External Login:", formState.external_login)}
            {renderTextRow("Additional Comments:", formState.additional_comments)}
          </Box>

          {/* RIGHT COLUMN */}
          <Box>
            {renderTextRow("Signal Strategy:", formState.signal_strategy)}
            {renderTextRow("Attached File:", formState.attached_file)}
            {renderTextRow("Platform Type:", formState.platform_type)}
            {renderTextRow("External Password:", formState.external_password)}
            
            {/* REASON (FEEDBACK) */}
            {(() => {
              const field = getField("reason_feedback");
              if (!field) return null;
              const value = formState[field.name] || "";
              return (
                <Box sx={{ mb: 1.25 }}>
                  <Typography sx={statementReasonTitleSx}>
                    Reason (Feedback)
                  </Typography>
                  <TitledInput
                    name={field.name}
                    title=""
                    value={value}
                    handleChange={handleChange}
                    type="text"
                    placeholder="Enter feedback"
                    error={false}
                    helperText=""
                    disabled={false}
                    inputProps={{
                      maxLength: 300,
                      style: {
                        height: 0,
                        borderRadius: 8,
                        border: `1px solid ${Colors.Secondary_400}`,
                      },
                    }}
                  />
                </Box>
              );
            })()}
          </Box>
        </Box>
      </DialogContent>

      {/* ACTIONS */}
      <DialogActions sx={actionsSx}>
        <Button onClick={onClose} variant="outlined" sx={cancelButtonSx}>
          {cancelLabel}
        </Button>
        <Button3px HandleOnClick={handleSubmit} label={confirmLabel} {...continueButtonProps} />
      </DialogActions>
    </Dialog>
  );
};

StatementRequestModal.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string,
  fields: PropTypes.array,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
};

export default StatementRequestModal;